<template>
  <div class="container-fluid">
    <div class="row mobile flex-nowrap">
      <SideBar id="desktop" />
      <MobileSideBar id="mobile" />

      <main class="col employee-content px-4 py-4">
        <section class="employee-section">
          <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <h2 class="employee-title mb-1">TIW admin access</h2>
            </div>
            <button
              type="button"
              class="btn btn-outline-dark btn-rounded"
              @click="loadAdminDirectory(true)"
              :disabled="isLoading"
            >
              Refresh
            </button>
          </div>

          <div class="employee-meta mb-4">
            <div><strong>Signed in as:</strong> {{ currentUserEmail || 'Unknown' }}</div>
            <div><strong>Role:</strong> {{ currentUserRoleLabel }}</div>
            <div><strong>Superadmin:</strong> {{ isSuperadmin ? 'Yes' : 'No' }}</div>
          </div>

          <div v-if="feedbackMessage" :class="['alert', feedbackVariantClass, 'mb-4']" role="alert">
            {{ feedbackMessage }}
          </div>

          <div v-if="isSuperadmin" class="employee-tools mb-4">
            <h5 class="mb-3">Add allowed TIW admin</h5>
            <form class="row g-3 align-items-end" @submit.prevent="submitAdminUser">
              <div class="col-12 col-lg-5">
                <label class="form-label" for="admin-email-input">Email</label>
                <input
                  id="admin-email-input"
                  v-model.trim="form.email"
                  type="email"
                  class="form-control"
                  autocomplete="email"
                  placeholder="name@example.com"
                  :disabled="isSaving"
                />
              </div>
              <div class="col-12 col-lg-3">
                <label class="form-label" for="admin-role-input">Role</label>
                <select
                  id="admin-role-input"
                  v-model="form.role"
                  class="form-select"
                  :disabled="isSaving"
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              <div class="col-12 col-lg-4 d-flex gap-2">
                <button type="submit" class="btn btn-primary btn-rounded" :disabled="isSaving || !form.email">
                  {{ isSaving ? 'Saving...' : 'Save admin' }}
                </button>
                <button type="button" class="btn btn-outline-secondary btn-rounded" @click="resetForm" :disabled="isSaving">
                  Clear
                </button>
              </div>
            </form>
          </div>

          <div class="employee-section-header mb-3">
            <h5 class="mb-1">Allowed TIW admins</h5>
          </div>

          <div v-if="isLoading && !adminUsers.length" class="py-4 text-muted">Loading admins...</div>
          <div v-else-if="isLoading" class="pb-3 text-muted small">Refreshing admin list...</div>

          <div v-if="adminUsers.length || !isLoading" class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Added by</th>
                  <th scope="col">Updated</th>
                  <th v-if="isSuperadmin" scope="col" class="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="adminUser in adminUsers" :key="adminUser.id">
                  <td>{{ adminUser.email }}</td>
                  <td>{{ formatRole(adminUser.role) }}</td>
                  <td>
                    <span :class="['status-badge', adminUser.enabled ? 'status-enabled' : 'status-disabled']">
                      {{ adminUser.enabled ? 'Enabled' : 'Disabled' }}
                    </span>
                  </td>
                  <td>{{ adminUser.addedBy || '-' }}</td>
                  <td>{{ formatUpdated(adminUser) }}</td>
                  <td v-if="isSuperadmin" class="text-end">
                    <button
                      v-if="canToggleAdminUser(adminUser)"
                      type="button"
                      class="btn btn-sm btn-outline-dark btn-rounded"
                      @click="toggleAdminUser(adminUser)"
                      :disabled="isSaving"
                    >
                      {{ adminUser.enabled ? 'Disable' : 'Enable' }}
                    </button>
                    <span v-else class="text-muted small">Protected</span>
                  </td>
                </tr>
                <tr v-if="!adminUsers.length">
                  <td :colspan="isSuperadmin ? 6 : 5" class="text-muted">No TIW admin users found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'

import SideBar from '@/components/SideBar.vue'
import MobileSideBar from '@/components/MobileSideBar.vue'
import {
  canManageAdminDirectory,
  evaluateTiwAdminAccess,
  listBootstrapAdminRecords,
  listEffectiveAdminRecords,
  saveAdminDirectoryUser,
  setAdminDirectoryUserEnabled
} from '@/services/adminDirectory'

export default {
  name: 'Employee',
  components: {
    SideBar,
    MobileSideBar
  },
  data() {
    return {
      adminUsers: [],
      currentUserEmail: '',
      currentUserRole: '',
      isLoading: false,
      isSaving: false,
      feedbackMessage: '',
      feedbackVariant: 'success',
      form: {
        email: '',
        role: 'admin'
      }
    }
  },
  computed: {
    isSuperadmin() {
      return canManageAdminDirectory(this.currentUserEmail) || this.currentUserRole === 'superadmin'
    },
    currentUserRoleLabel() {
      if (!this.currentUserRole) {
        return this.isSuperadmin ? 'Superadmin' : 'Admin'
      }

      return this.formatRole(this.currentUserRole)
    },
    feedbackVariantClass() {
      return this.feedbackVariant === 'danger' ? 'alert-danger' : 'alert-success'
    }
  },
  created() {
    const currentUser = firebase.auth().currentUser
    if (currentUser?.email) {
      this.currentUserEmail = currentUser.email.toLowerCase()
    }

    this.loadAdminDirectory()
  },
  methods: {
    async loadAdminDirectory(forceRefresh = false) {
      this.feedbackMessage = ''
      this.isLoading = true
      this.adminUsers = listBootstrapAdminRecords()

      try {
        if (this.currentUserEmail) {
          const accessDecision = await evaluateTiwAdminAccess(this.currentUserEmail)
          this.currentUserRole = accessDecision.role || (this.isSuperadmin ? 'superadmin' : 'admin')
        }

        this.adminUsers = await listEffectiveAdminRecords({ forceRefresh })
      } catch (error) {
        this.feedbackVariant = 'danger'
        this.feedbackMessage = error?.message || 'Failed to load the TIW admin directory.'
      } finally {
        this.isLoading = false
      }
    },
    resetForm() {
      this.form.email = ''
      this.form.role = 'admin'
    },
    formatRole(role) {
      if (role === 'superadmin') {
        return 'Superadmin'
      }

      return 'Admin'
    },
    formatUpdated(adminUser) {
      if (!adminUser.updatedAt) {
        return '-'
      }

      return adminUser.updatedBy
        ? `${adminUser.updatedAt} by ${adminUser.updatedBy}`
        : adminUser.updatedAt
    },
    canToggleAdminUser(adminUser) {
      return !String(adminUser.id || '').startsWith('bootstrap:')
    },
    async submitAdminUser() {
      if (!this.form.email || this.isSaving) {
        return
      }

      this.feedbackMessage = ''
      this.isSaving = true

      try {
        await saveAdminDirectoryUser({
          email: this.form.email,
          role: this.form.role,
          actorEmail: this.currentUserEmail
        })

        this.feedbackVariant = 'success'
        this.feedbackMessage = `${this.form.email} is now allowed to use TIW.`
        this.resetForm()
        await this.loadAdminDirectory(true)
      } catch (error) {
        this.feedbackVariant = 'danger'
        this.feedbackMessage = error?.message || 'Failed to save the TIW admin user.'
      } finally {
        this.isSaving = false
      }
    },
    async toggleAdminUser(adminUser) {
      if (!this.canToggleAdminUser(adminUser) || this.isSaving) {
        return
      }

      this.feedbackMessage = ''
      this.isSaving = true

      try {
        await setAdminDirectoryUserEnabled({
          id: adminUser.id,
          enabled: !adminUser.enabled,
          actorEmail: this.currentUserEmail
        })

        this.feedbackVariant = 'success'
        this.feedbackMessage = `${adminUser.email} was ${adminUser.enabled ? 'disabled' : 'enabled'} in the TIW admin directory.`
        await this.loadAdminDirectory(true)
      } catch (error) {
        this.feedbackVariant = 'danger'
        this.feedbackMessage = error?.message || 'Failed to update the TIW admin user.'
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped>
#mobile {
  display: none;
}

#desktop {
  max-width: 100%;
}

.employee-content {
  min-height: 100vh;
}

.employee-section {
  max-width: 1200px;
}

.employee-title {
  font-size: 1.75rem;
}

.employee-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
}

.employee-tools {
  padding: 1rem 1.25rem;
  border: 1px solid #d7dde4;
  border-radius: 8px;
  background: #ffffff;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-enabled {
  background: rgba(25, 135, 84, 0.12);
  color: #146c43;
}

.status-disabled {
  background: rgba(220, 53, 69, 0.12);
  color: #b02a37;
}

@media screen and (max-width: 768px) {
  #mobile {
    display: flex;
  }

  #desktop {
    display: none;
  }

  .mobile {
    display: grid;
  }

  .employee-content {
    padding-top: 1.25rem !important;
  }
}
</style>
